import { useState } from "react";
import { C, F, btnBase } from "../theme/tokens";
import { prezzoPerCadenza } from "./listino";

/**
 * La scheda di un piano, il suo pulsante e il selettore Mensile/Annuale,
 * uguali nella home e nell'area clienti: stessa tinta, stesse misure, stesso
 * prezzo in testa, stesse voci. Cambiano solo l'etichetta e l'azione del
 * pulsante, che decide chi usa la scheda.
 */

const ease = "cubic-bezier(.25,1,.5,1)";

/** Tinte metalliche appena percettibili: argento, oro, platino. Il platino
 *  e' un gradino piu' scuro dell'argento, o le due schede si confondono. */
export const TINTE = {
  silver: { sfondo: "linear-gradient(165deg, #ffffff 0%, #f4f6f9 55%, #e9edf2 100%)", bordo: "#d8dee6" },
  gold: { sfondo: "linear-gradient(165deg, #ffffff 0%, #fdfaf0 55%, #f6efd9 100%)", bordo: "#e6dbb3" },
  platinum: { sfondo: "linear-gradient(165deg, #f6f8fb 0%, #e2e8f0 50%, #c9d3df 100%)", bordo: "#b3c0d0" },
};

/** Il piano in evidenza nel listino ("PIU' SCELTO"). */
export const PIANO_IN_EVIDENZA = "silver";

const Spunta = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.smartBlue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export function SelettoreCadenza({ cadenza, onChange, style = {} }) {
  const opzione = (valore, testo) => {
    const scelta = cadenza === valore;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={scelta}
        onClick={() => onChange(valore)}
        style={{ padding: "9px 22px", borderRadius: 999, border: "none", background: scelta ? C.white : "transparent", color: scelta ? C.textTitle : C.textNum, fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: F.b, boxShadow: scelta ? "0 2px 8px rgba(24,46,79,0.12)" : "none", transition: `all 0.2s ${ease}` }}
      >
        {testo}
      </button>
    );
  };
  return (
    <div role="radiogroup" aria-label="Cadenza di fatturazione" style={{ display: "inline-flex", gap: 2, padding: 4, borderRadius: 999, background: "#e9f3f9", border: `1px solid ${C.border}`, ...style }}>
      {opzione("mensile", "Mensile")}
      {opzione("annuale", "Annuale")}
    </div>
  );
}

/**
 * Cosa propone il pulsante di una scheda a chi guarda il listino.
 *
 * Senza abbonamento ogni scheda si attiva. Con un abbonamento la scheda del
 * piano in corso, alla stessa cadenza, e' quella attuale (pulsante spento);
 * lo stesso piano all'altra cadenza propone il cambio di cadenza; i piani
 * sopra sono un passaggio in su, quelli sotto in giu'. L'ordine e' quello
 * del listino.
 *
 * @param {object} piano voce del listino
 * @param {"mensile"|"annuale"} cadenza quella scelta nel selettore
 * @param {{piano?: string, cadenza?: string}|null} azienda l'azienda abbonata, o null
 * @param {object[]} abbonabili i piani abbonabili, nell'ordine del listino
 * @returns {{tipo: "attiva"|"attuale"|"cadenza"|"su"|"giu", etichetta: string}}
 */
export function azionePiano(piano, cadenza, azienda, abbonabili) {
  const attuale = azienda?.piano;
  if (!attuale) return { tipo: "attiva", etichetta: `Attiva ${piano.nome}` };
  if (piano.chiave === attuale) {
    if ((azienda.cadenza || "mensile") === cadenza) return { tipo: "attuale", etichetta: "Piano attuale" };
    return { tipo: "cadenza", etichetta: cadenza === "annuale" ? "Passa all'annuale" : "Passa al mensile" };
  }
  const ordine = (abbonabili || []).map((p) => p.chiave);
  const su = ordine.indexOf(piano.chiave) > ordine.indexOf(attuale);
  return { tipo: su ? "su" : "giu", etichetta: `Passa a ${piano.nome}` };
}

const VARIANTI_BOTTONE = {
  // Il pulsante scuro del sito: blu notte a riposo, il gradiente celeste al passaggio.
  scuro: {
    riposo: { background: C.dataNight, color: "#fff", border: "1px solid transparent", boxShadow: "0 8px 24px rgba(0,156,221,0.18)" },
    sopra: { background: `linear-gradient(90deg, ${C.smartBlue} 0%, ${C.blueAccent} 100%)`, color: "#fff", border: "1px solid transparent", boxShadow: "0 14px 40px rgba(89,201,244,0.4)" },
  },
  blu: {
    riposo: { background: C.smartBlue, color: "#fff", border: "1px solid transparent", boxShadow: "0 8px 24px rgba(0,156,221,0.28)" },
    sopra: { background: "#0088c4", color: "#fff", border: "1px solid transparent", boxShadow: "0 14px 40px rgba(0,156,221,0.35)" },
  },
  chiaro: {
    riposo: { background: C.white, color: C.textTitle, border: `1px solid ${C.border}`, boxShadow: "none" },
    sopra: { background: C.smartBlue10, color: C.smartBlue, border: `1px solid ${C.smartBlue}`, boxShadow: "none" },
  },
  // Il piano in corso: si vede, non si preme.
  spento: {
    riposo: { background: C.smartBlue20, color: C.textNum, border: `1px solid ${C.smartBlue30}`, boxShadow: "none" },
    sopra: { background: C.smartBlue20, color: C.textNum, border: `1px solid ${C.smartBlue30}`, boxShadow: "none" },
  },
};

/** Il pulsante in fondo alla scheda: stesse misure e stesso carattere ovunque. */
export function BottonePiano({ etichetta, onClick, disabilitato = false, variante = "scuro" }) {
  const [sopra, setSopra] = useState(false);
  const stile = VARIANTI_BOTTONE[variante] || VARIANTI_BOTTONE.scuro;
  const fermo = disabilitato || variante === "spento";
  return (
    <button
      type="button"
      onClick={fermo ? undefined : onClick}
      disabled={fermo}
      onMouseEnter={() => setSopra(true)}
      onMouseLeave={() => setSopra(false)}
      onFocus={() => setSopra(true)}
      onBlur={() => setSopra(false)}
      style={{
        width: "100%", height: 46, borderRadius: 12, padding: "0 12px",
        fontFamily: btnBase.fontFamily, fontWeight: btnBase.fontWeight, fontSize: 12, letterSpacing: btnBase.letterSpacing,
        textTransform: "uppercase", whiteSpace: "nowrap", cursor: fermo ? "default" : "pointer",
        transition: `background 0.35s ${ease}, box-shadow 0.35s ${ease}, color 0.2s ${ease}`,
        ...(sopra && !fermo ? stile.sopra : stile.riposo),
        opacity: disabilitato && variante !== "spento" ? 0.6 : 1,
      }}
    >
      {etichetta}
    </button>
  );
}

/**
 * @param {{
 *   piano: object, cadenza: "mensile"|"annuale", tinta?: {sfondo: string, bordo: string},
 *   evidenza?: boolean, etichettaAlto?: string|null, bottone: import("react").ReactNode, isMobile?: boolean,
 * }} props evidenza: anello azzurro (un'ombra, non un bordo piu' spesso: le
 *   schede restano tutte della stessa misura); etichettaAlto: il cartellino
 *   in cima ("PIU' SCELTO", "IL TUO PIANO", "UPGRADE")
 */
export function SchedaPiano({ piano, cadenza, tinta, evidenza = false, etichettaAlto = null, bottone, isMobile = false }) {
  const [hover, setHover] = useState(false);
  const prezzo = prezzoPerCadenza(piano, cadenza);
  const bordo = tinta ? tinta.bordo : C.border;
  const anello = evidenza ? `0 0 0 1px ${C.smartBlue}, ` : "";
  const sotto = piano.per || `${prezzo.nota || ""}${prezzo.dettaglio ? ` · ${prezzo.dettaglio}` : ""}`;
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", flexDirection: "column", minWidth: 0,
        background: tinta ? tinta.sfondo : C.white,
        color: C.textTitle,
        borderRadius: 20, padding: isMobile ? "22px 20px 18px" : "26px 22px 20px",
        border: `1px solid ${evidenza ? C.smartBlue : bordo}`,
        boxShadow: `${anello}${hover ? "0 20px 48px rgba(24,46,79,0.12)" : C.shadowMd}`,
        transform: hover ? "translateY(-4px)" : "translateY(0)",
        transition: `transform 0.35s ${ease}, box-shadow 0.35s ${ease}`,
        position: "relative",
      }}
    >
      {etichettaAlto && (
        <span style={{ position: "absolute", top: -12, left: 18, background: C.smartBlue, color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", padding: "4px 10px", borderRadius: 999, fontFamily: F.b, whiteSpace: "nowrap" }}>
          {etichettaAlto}
        </span>
      )}
      <h3 style={{ fontFamily: F.h, fontSize: 22, fontWeight: 600, letterSpacing: "0.1em", margin: "0 0 14px", textTransform: "uppercase", color: C.textTitle }}>{piano.nome}</h3>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontFamily: F.h, fontSize: 34, fontWeight: 800, letterSpacing: "-0.5px", lineHeight: 1.05 }}>{prezzo.importo}</span>
        {prezzo.unita && <span style={{ fontSize: 13, color: C.textNum, fontFamily: F.b }}>{prezzo.unita}</span>}
        {prezzo.risparmio && (
          <span style={{ background: C.smartBlue20, color: C.smartBlue, border: `1px solid ${C.smartBlue30}`, borderRadius: 999, padding: "3px 10px", fontSize: 12, fontWeight: 700, fontFamily: F.b, whiteSpace: "nowrap" }}>{prezzo.risparmio}</span>
        )}
      </div>
      <p style={{ margin: "6px 0 0", fontSize: 12, color: C.textNum, fontFamily: F.b, lineHeight: 1.45, textWrap: "balance" }}>{sotto}</p>
      {/* Le utenze non stanno piu' qui: sono fra le voci ("Fino a 3 utenze"). */}
      <div style={{ margin: "16px 0 14px", paddingTop: 14, borderTop: `1px solid ${bordo}`, fontSize: 13, fontFamily: F.b, lineHeight: 1.5, color: C.textBody }}>
        <div style={{ fontWeight: 600 }}>{piano.pagine}</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9, flex: "1 1 auto", marginBottom: 20 }}>
        {piano.voci.map((v) => (
          <div key={v.testo} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ flexShrink: 0, marginTop: 2, display: "inline-flex" }}><Spunta /></span>
            <span style={{ fontSize: 13, lineHeight: "20px", color: C.textNum, fontFamily: F.b }}>
              {v.testo}{!v.disponibile && <em style={{ opacity: 0.8 }}> (in arrivo)</em>}
            </span>
          </div>
        ))}
      </div>
      {bottone}
    </div>
  );
}
